import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import type { PublicUser } from '@private-messenger/shared';

import { api, ApiClientError } from '../api.js';

interface AdminPageProps {
  currentUser: PublicUser;
}

export function AdminPage({ currentUser }: AdminPageProps) {
  const queryClient = useQueryClient();
  const users = useQuery({ queryKey: ['admin-users'], queryFn: api.adminUsers });
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({
    login: '',
    displayName: '',
    password: '',
    role: 'USER' as const,
  });

  const refresh = async () => queryClient.invalidateQueries({ queryKey: ['admin-users'] });
  const create = useMutation({
    mutationFn: api.createUser,
    onSuccess: async () => {
      setForm({ login: '', displayName: '', password: '', role: 'USER' });
      setError(null);
      await refresh();
    },
    onError: (reason) => setError(reason instanceof Error ? reason.message : 'Ошибка'),
  });
  const block = useMutation({
    mutationFn: ({ id, blocked }: { id: string; blocked: boolean }) => api.setBlocked(id, blocked),
    onSuccess: refresh,
  });
  const reset = useMutation({
    mutationFn: ({ id, password }: { id: string; password: string }) =>
      api.resetPassword(id, password),
    onSuccess: () => setError(null),
    onError: (reason) =>
      setError(reason instanceof ApiClientError ? reason.message : 'Не удалось сбросить пароль'),
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    create.mutate(form);
  };
  const resetPassword = (user: PublicUser) => {
    const password = window.prompt(
      `Новый временный пароль для ${user.displayName} (минимум 12 символов):`,
    );
    if (password) reset.mutate({ id: user.id, password });
  };

  return (
    <main className="settings-page">
      <header className="settings-header">
        <Link to="/" className="back-link">
          ‹ Назад
        </Link>
        <div>
          <h1>Сотрудники</h1>
          <p>Добавление сотрудников и управление доступом</p>
        </div>
        <span className="avatar blue">{currentUser.displayName[0]}</span>
      </header>
      <div className="settings-grid">
        <section className="settings-card">
          <h2>Добавить сотрудника</h2>
          <p className="card-hint">Создайте логин и временный пароль.</p>
          <form className="settings-form" onSubmit={submit}>
            <label>
              Логин
              <input
                value={form.login}
                onChange={(event) => setForm({ ...form, login: event.target.value })}
                placeholder="ivan.petrov"
                required
              />
            </label>
            <label>
              Отображаемое имя
              <input
                value={form.displayName}
                onChange={(event) => setForm({ ...form, displayName: event.target.value })}
                placeholder="Иван Петров"
                required
              />
            </label>
            <label>
              Временный пароль
              <input
                type="password"
                minLength={12}
                value={form.password}
                onChange={(event) => setForm({ ...form, password: event.target.value })}
                required
              />
            </label>
            {error && <p className="form-error">{error}</p>}
            <button className="primary-button" disabled={create.isPending}>
              Добавить сотрудника
            </button>
          </form>
        </section>
        <section className="settings-card users-card">
          <div className="card-title">
            <h2>Список сотрудников</h2>
            <span>{users.data?.users.length ?? 0}</span>
          </div>
          {users.isLoading && <p className="empty-state">Загружаем…</p>}
          {users.isError && <p className="form-error">Не удалось получить список</p>}
          <div className="user-rows">
            {users.data?.users.map((user) => (
              <article className="user-row" key={user.id}>
                <span className={`avatar ${user.isBlocked ? 'muted-avatar' : 'violet'}`}>
                  {user.displayName[0]}
                </span>
                <span className="user-main">
                  <strong>{user.displayName}</strong>
                  <small>@{user.login}</small>
                </span>
                <span className={`status-pill ${user.isBlocked ? 'blocked' : ''}`}>
                  {user.isBlocked ? 'Заблокирован' : 'Активен'}
                </span>
                <div className="row-actions">
                  <button onClick={() => resetPassword(user)}>Сбросить пароль</button>
                  <button
                    disabled={user.id === currentUser.id || block.isPending}
                    onClick={() => block.mutate({ id: user.id, blocked: !user.isBlocked })}
                  >
                    {user.isBlocked ? 'Разблокировать' : 'Заблокировать'}
                  </button>
                </div>
              </article>
            ))}
          </div>
        </section>
      </div>
    </main>
  );
}
